/**
 * useLiveVisitorFeed
 *
 * The realtime feed for one site (or up to five in a workspace), parsed and
 * grouped into visits. Polls every 5s like the Overview live card, so a
 * visitor profile page keeps growing while the visitor is still on site.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getAggregateTrafficRealtime } from '@/lib/api/external-api'
import { buildVisitorProfiles, parseRealtimeFeed } from '@/lib/realtime/visitorProfiles'

export function useLiveVisitorFeed(propertyIds: string[], token: string | undefined) {
  const ids = propertyIds.filter(Boolean)
  const enabled = ids.length > 0 && !!token

  const query = useQuery({
    queryKey: ['live-visitor-feed', ids],
    queryFn: async () => {
      const res = await getAggregateTrafficRealtime(ids, token!)
      return parseRealtimeFeed({
        liveFeed: res.data?.live_feed || [],
        sessions: res.data?.sessions || [],
        metrics: res.data?.metrics || [],
      })
    },
    enabled,
    refetchInterval: 5_000,
    staleTime: 0,
    retry: false,
  })

  const profiles = useMemo(() => buildVisitorProfiles(query.data ?? []), [query.data])

  return {
    profiles,
    isLoading: enabled && query.isLoading,
    isError: query.isError,
  }
}
